import { forwardRef, useId, type InputHTMLAttributes, type SelectHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

const FIELD =
  'w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-fg ' +
  'placeholder:text-faint transition-colors focus:border-tech focus:outline-none ' +
  'focus:ring-2 focus:ring-tech/30 disabled:opacity-60';

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & FieldProps>(
  ({ label, hint, error, className, id, ...props }, ref) => {
    const generated = useId();
    const inputId = id ?? generated;
    const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
    return (
      <div className="space-y-1.5">
        {label ? (
          <label htmlFor={inputId} className="block text-sm font-medium text-fg">
            {label}
            {props.required ? <span className="ml-1 text-danger">*</span> : null}
          </label>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(FIELD, error && 'border-danger focus:border-danger focus:ring-danger/30', className)}
          {...props}
        />
        {error ? (
          <p id={`${inputId}-error`} className="text-xs text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="text-xs text-faint">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & FieldProps
>(({ label, hint, className, id, children, ...props }, ref) => {
  const generated = useId();
  const selectId = id ?? generated;
  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={selectId} className="block text-sm font-medium text-fg">
          {label}
        </label>
      ) : null}
      <select ref={ref} id={selectId} className={cn(FIELD, 'pr-8', className)} {...props}>
        {children}
      </select>
      {hint ? <p className="text-xs text-faint">{hint}</p> : null}
    </div>
  );
});
Select.displayName = 'Select';
